import React from 'react';
import PropTypes from 'prop-types';
import { Button, Snackbar } from '@mui/material';

import i18n from '../../../i18n';

const UNDO_MS = 6000;

/**
 * The note after a change to the grid's column layout — a column resized,
 * fitted or moved, the layout reset — with a button that takes it back.
 * Bottom centre — clear of the side navigation (left) and of the app's
 * alerts (right). Stays put while the user goes on working in the grid (a
 * click elsewhere does not close it); each new change brings it up afresh.
 * Its message is announced to screen readers, which also reports a column
 * moved from the keyboard.
 *
 * @param {Object} props - Component props.
 * @param {Object|null} props.undo - `{ key, message }` of the change, or null.
 * @param {Function} props.onUndo - Takes the change back.
 * @param {Function} props.onClose - The note is done (timed out).
 * @returns {JSX.Element} the snackbar
 */
export default function GridColumnLayoutUndo({ undo, onUndo, onClose }) {
    return (
        <Snackbar
            key={undo?.key}
            open={Boolean(undo)}
            autoHideDuration={UNDO_MS}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            onClose={(event, reason) => {
                if (reason !== 'clickaway') onClose();
            }}
            message={undo?.message}
            action={
                <Button
                    color="inherit"
                    size="small"
                    onClick={onUndo}
                    data-testid="grid-column-layout-undo"
                >
                    {i18n.t('common.undo')}
                </Button>
            }
        />
    );
}

GridColumnLayoutUndo.propTypes = {
    undo: PropTypes.shape({
        key: PropTypes.number,
        message: PropTypes.string
    }),
    onUndo: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

GridColumnLayoutUndo.defaultProps = {
    undo: null
};
